import { useCallback, useEffect, useState } from "react";
import { Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RevertDialog } from "@/components/inventory/RevertDialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

export const MovementsTab = () => {
  const { meta, can } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [rev, setRev] = useState(null);
  const load = useCallback(() => api.get("/inventory/movements").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const t = q.trim().toLowerCase();
  const shown = t ? rows.filter((m) => [m.folio, m.item_label, m.reference, ...(m.serials || [])].some((x) => (x || "").toLowerCase().includes(t))) : rows;
  return (
    <div className="space-y-3">
    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por serie / IMEI, folio de OS o artículo…" className="h-10 max-w-sm" data-testid="movements-search" />
    <Table data-testid="inventory-movements-table">
      <TableHeader><TableRow>{["Fecha", "Movimiento", "Artículo", "Cantidad", "Ubicación", "OS / Referencia", "Motivo", "Usuario", ""].map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
      <TableBody>{shown.map((m) => (
        <TableRow key={m.id} data-testid={`mv-row-${m.id}`}>
          <TableCell className="whitespace-nowrap text-xs">{fmtDateTime(m.at)}</TableCell>
          <TableCell className="text-sm">{meta?.movement_kinds?.[m.kind] || m.kind}</TableCell>
          <TableCell className="text-sm">{m.item_label}{m.serials?.length > 0 && <span className="block font-mono text-[11px] text-muted-foreground">S/N: {m.serials.join(", ")}</span>}</TableCell>
          <TableCell className="font-mono text-sm">{m.quantity} {m.unit}</TableCell>
          <TableCell className="text-xs">{m.location_path}</TableCell>
          <TableCell className="text-xs">{[m.folio, m.reference].filter(Boolean).join(" · ") || "—"}</TableCell>
          <TableCell className="text-xs">{m.reason || "—"}</TableCell>
          <TableCell className="text-xs">{m.user_name}</TableCell>
          <TableCell className="whitespace-nowrap text-xs">
            {m.kind === "consumo" && (m.reverted_by_movement_id
              ? <span className="rounded border border-amber-500/40 px-1.5 text-amber-400" data-testid={`mv-reverted-${m.id}`}>Revertido</span>
              : can("inventory.write") && <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => setRev(m)} data-testid={`mv-revert-${m.id}`}><Undo2 className="mr-1 h-3 w-3" />Revertir</Button>)}
            {m.kind === "reversion" && <span className="text-muted-foreground">{m.destination === "tecnico" ? `A ${m.responsible_name}` : "Al almacén"}</span>}
          </TableCell>
        </TableRow>))}
      </TableBody>
    </Table>
    <RevertDialog mv={rev} onClose={() => setRev(null)} onDone={load} />
    </div>
  );
};

const STATUS = { almacen: "En almacén", salida: "Salida de almacén", instalado: "Instalado" };

export const SerialsTab = () => {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  useEffect(() => { const t = setTimeout(() => api.get("/inventory/serials", { params: { q } }).then((r) => setRows(r.data)), 250); return () => clearTimeout(t); }, [q]);
  return (
    <div className="space-y-3" data-testid="inventory-serials-tab">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar número de serie…" className="h-10 max-w-xs" data-testid="serial-search" />
      <Table data-testid="inventory-serials-table">
        <TableHeader><TableRow>{["Número de serie", "Artículo", "Estado", "Ubicación / OS", "Cliente", "Instalación"].map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
        <TableBody>{rows.map((s) => (
          <TableRow key={s.id} data-testid={`serial-row-${s.serial}`}>
            <TableCell className="font-mono text-sm">{s.serial}</TableCell>
            <TableCell className="text-sm">{s.item_label}</TableCell>
            <TableCell className="text-sm">{STATUS[s.status] || s.status}</TableCell>
            <TableCell className="text-xs">{s.status === "almacen" ? s.location_path : s.folio || "—"}</TableCell>
            <TableCell className="text-xs">{s.client_name || "—"}</TableCell>
            <TableCell className="text-xs">{s.installed_at ? fmtDateTime(s.installed_at) : "—"}</TableCell>
          </TableRow>))}
        </TableBody>
      </Table>
    </div>
  );
};
